import { useCallback, useState } from "react"
import { useSigningRecords, useSignDta, useVtmfFilings } from "../../../hooks/queries/useDtas"

/**
 * Composes the signing-records and vTMF-filings query hooks with the sign-DTA mutation and the sign
 * modal's local UI state. Reads and cache invalidation live in the TanStack Query hooks; this hook
 * owns only the modal state and adapts them to the page's existing API.
 */
export function useSigningWorkflow(dtaId: string | undefined, onSuccess: () => void) {
  const signingRecordsQuery = useSigningRecords(dtaId)
  const filingsQuery = useVtmfFilings(dtaId)
  const signMutation = useSignDta(dtaId ?? "")

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [comment, setComment] = useState("")
  const [error, setError] = useState<string | null>(null)

  const openModal = useCallback(() => setIsModalOpen(true), [])
  const closeModal = useCallback(() => setIsModalOpen(false), [])

  const fetchSigningAndFilings = useCallback(async () => {
    await Promise.all([signingRecordsQuery.refetch(), filingsQuery.refetch()])
  }, [signingRecordsQuery, filingsQuery])

  const handleSign = useCallback(async () => {
    if (!dtaId) return
    setError(null)
    try {
      await signMutation.mutateAsync({
        method: "ELECTRONIC_TEXT",
        attestation_version: "1.0",
        comment: comment || "Electronic approval",
      })
      setIsModalOpen(false)
      setComment("")
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to sign")
    }
  }, [dtaId, comment, onSuccess, signMutation])

  return {
    signingRecords: signingRecordsQuery.data ?? [],
    filings: filingsQuery.data ?? [],
    isModalOpen,
    openModal,
    closeModal,
    comment,
    setComment,
    submitting: signMutation.isPending,
    error,
    handleSign,
    fetchSigningAndFilings,
  }
}
